import React, { useCallback, useMemo, useState } from 'react'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
    DASHBOARD_TABLE_ROW_CLASS_NAME,
    DASHBOARD_TABLE_SIZE,
    dashboardTableClassName,
} from '../../components/dashboard-table-parts'
import { DashboardTableCard } from '../../components/dashboard-table-card'
import { DefinitionTooltip } from '../../components/definition-tooltip'
import { DeltaText } from '../../components/delta-text'
import { OutlierTag } from '../../components/outlier-tag'
import { DomainSearch, domainMatchesSearchTerm } from '../../components/domain-search'
import { NoDomains, NoDomainsMatchSearch } from '../../components/domain-empty-states'
import { SectionError } from '../../components/section-error'
import { DASHBOARD_TABLE_PAGE_SIZE } from '../../components/table-page-size'
import { TABLE_PLACEHOLDER_ROW_COUNT } from '../../components/table-placeholder-rows'
import { ACQUISITION_TABLE_ROW_HEIGHT, tableRowHeightStyle } from '../../components/table-row-heights'
import { TableSkeleton } from '../../components/table-skeleton'
import { TableSectionWell } from '../../components/section-well'
import { TABLE_TOTALS_ROW_CLASS_NAME, TotalsFigure, TotalsLabel } from '../../components/table-totals'
import { DashboardDomain, GoodDirection } from '../../types'
import { formatCountInFull, formatRate } from '../../math/format'
import { DailyRow } from '../../math/series'
import { DateWindow } from '../../math/windows'
import { AcquisitionRow, buildAcquisitionRows, buildAcquisitionTotals } from './acquisition-model'

const OWN_AVERAGE_DEFINITION =
    "The range's subscription rate against the domain's own trailing 28-day rate, in percentage points. A domain is never compared with another."

type Props = {
    status: 'loading' | 'ready' | 'error'
    rows: DailyRow[]
    domains: DashboardDomain[]
    /** Empty means every domain. */
    selectedDomainIds: number[]
    range: DateWindow
    goodDirectionFor: (measure: string) => GoodDirection | undefined
    onRetry: () => void
}

/**
 * The column compares a subscription rate (subscriptions over permissions shown), which the registry has no measure
 * for. A rate moves with its numerator, so it takes the direction of subscriptions, as the subscriber figures do.
 */
const SUBSCRIPTION_RATE_GOOD_DIRECTION_MEASURE = 'subscriptions'

/*
 * Every column but the domain, whose name and hostname are free text, has a fixed width sized to its heading and
 * longest figure, so the loaded table and its loading placeholder lay out identically; the domain takes what is left.
 */
const ACQUISITION_TABLE_CLASS_NAME = 'org-dashboard-acquisition-table'
export const ACQUISITION_DOMAIN_MINIMUM_WIDTH = 240
export const ACQUISITION_PERMISSIONS_COLUMN_WIDTH = 160
export const ACQUISITION_SUBSCRIPTIONS_COLUMN_WIDTH = 144
export const ACQUISITION_RATE_COLUMN_WIDTH = 160
export const ACQUISITION_COMPARISON_COLUMN_WIDTH = 240
export const ACQUISITION_TABLE_MINIMUM_WIDTH =
    ACQUISITION_DOMAIN_MINIMUM_WIDTH +
    ACQUISITION_PERMISSIONS_COLUMN_WIDTH +
    ACQUISITION_SUBSCRIPTIONS_COLUMN_WIDTH +
    ACQUISITION_RATE_COLUMN_WIDTH +
    ACQUISITION_COMPARISON_COLUMN_WIDTH

/** The columns that hold a label or a figure in the Total row; the comparison column is blank there. */
const ACQUISITION_TOTALS_SHAPES = {
    domain: 'label',
    permissions_shown: 'figure',
    subscriptions: 'figure',
    subscription_rate: 'figure',
} as const

const columnsFor = (goodDirection: GoodDirection | undefined): ColumnsType<AcquisitionRow> => [
    {
        key: 'domain',
        title: 'Domain',
        render: (_, row) => (
            <div className="org-dashboard-domain-cell-text">
                <div>{row.domain.displayName}</div>
                <div className="org-dashboard-domain-cell-hostname">{row.domain.hostname}</div>
            </div>
        ),
    },
    {
        key: 'permissions_shown',
        title: 'Permissions Shown',
        align: 'right',
        width: ACQUISITION_PERMISSIONS_COLUMN_WIDTH,
        render: (_, row) => formatCountInFull(row.permissionsShown),
    },
    {
        key: 'subscriptions',
        title: 'Subscriptions',
        align: 'right',
        width: ACQUISITION_SUBSCRIPTIONS_COLUMN_WIDTH,
        render: (_, row) => formatCountInFull(row.subscriptions),
    },
    {
        key: 'subscription_rate',
        title: 'Subscription Rate',
        align: 'right',
        width: ACQUISITION_RATE_COLUMN_WIDTH,
        render: (_, row) => <strong>{formatRate(row.rate)}</strong>,
    },
    {
        key: 'vs_own_average',
        width: ACQUISITION_COMPARISON_COLUMN_WIDTH,
        title: (
            <span>
                vs Own Avg
                <DefinitionTooltip text={OWN_AVERAGE_DEFINITION} />
            </span>
        ),
        render: (_, row) => (
            <div className="org-dashboard-acquisition-comparison">
                <DeltaText
                    value={row.vsOwnAvgPts}
                    kind="rate"
                    goodDirection={goodDirection}
                    steady={false}
                    comparisonLabel="vs own 28-day average"
                />
                {row.outlier ? <OutlierTag days={row.outlierDays} operational={false} /> : null}
            </div>
        ),
    },
]

export const AcquisitionSection: React.FC<Props> = ({
    status,
    rows,
    domains,
    selectedDomainIds,
    range,
    goodDirectionFor,
    onRetry,
}) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)

    const acquisitionRows = useMemo(
        () => (status === 'ready' ? buildAcquisitionRows({ rows, domains, selectedDomainIds, range }) : []),
        [status, rows, domains, selectedDomainIds, range],
    )

    // The Total row follows the page's domain selector and not the search, which only chooses what is listed.
    const totals = useMemo(() => buildAcquisitionTotals(acquisitionRows), [acquisitionRows])

    const trimmedSearchTerm = searchTerm.trim()
    const visibleRows = useMemo(
        () => acquisitionRows.filter((row) => domainMatchesSearchTerm(row.domain, trimmedSearchTerm)),
        [acquisitionRows, trimmedSearchTerm],
    )
    const changeSearchTerm = useCallback((nextSearchTerm: string) => {
        setSearchTerm(nextSearchTerm)
        setCurrentPage(1)
    }, [])

    const goodDirection = goodDirectionFor(SUBSCRIPTION_RATE_GOOD_DIRECTION_MEASURE)
    const columns = useMemo(() => columnsFor(goodDirection), [goodDirection])

    const filters =
        status === 'loading' || (status === 'ready' && acquisitionRows.length) ? (
            <DomainSearch
                searchTerm={searchTerm}
                onSearchTermChange={changeSearchTerm}
                matchedDomainCount={visibleRows.length}
                totalDomainCount={acquisitionRows.length}
                accessibleName="Search acquisition domains"
                disabled={status === 'loading'}
            />
        ) : undefined

    return (
        <TableSectionWell sectionKey="acquisition">
            <DashboardTableCard sectionKey="acquisition" title="Acquisition" filters={filters}>
                {status === 'error' ? (
                    <SectionError sectionName="Acquisition" onRetry={onRetry} />
                ) : status === 'loading' ? (
                    <TableSkeleton
                        columns={columns}
                        shapes={{ domain: 'two-lines' }}
                        rowCount={TABLE_PLACEHOLDER_ROW_COUNT}
                        rowHeight={ACQUISITION_TABLE_ROW_HEIGHT}
                        className={ACQUISITION_TABLE_CLASS_NAME}
                        tableLayout="fixed"
                        scroll={{ x: ACQUISITION_TABLE_MINIMUM_WIDTH }}
                        totalsShapes={ACQUISITION_TOTALS_SHAPES}
                    />
                ) : acquisitionRows.length === 0 ? (
                    <NoDomains />
                ) : (
                    <Table<AcquisitionRow>
                        className={dashboardTableClassName(ACQUISITION_TABLE_CLASS_NAME)}
                        size={DASHBOARD_TABLE_SIZE}
                        rowClassName={DASHBOARD_TABLE_ROW_CLASS_NAME}
                        aria-label="Acquisition"
                        rowKey={(row) => row.domain.id}
                        columns={columns}
                        dataSource={visibleRows}
                        tableLayout="fixed"
                        scroll={{ x: ACQUISITION_TABLE_MINIMUM_WIDTH }}
                        style={tableRowHeightStyle(ACQUISITION_TABLE_ROW_HEIGHT)}
                        locale={{
                            emptyText: (
                                <NoDomainsMatchSearch
                                    searchTerm={trimmedSearchTerm}
                                    onClearSearch={() => changeSearchTerm('')}
                                />
                            ),
                        }}
                        pagination={{
                            current: currentPage,
                            pageSize: DASHBOARD_TABLE_PAGE_SIZE,
                            showSizeChanger: false,
                            showTotal: (total, [from, to]) => `${from}–${to} of ${total} domains`,
                            hideOnSinglePage: true,
                            placement: ['bottomEnd'],
                        }}
                        onChange={(pagination) => setCurrentPage(pagination.current ?? 1)}
                        summary={() => (
                            <Table.Summary fixed="bottom">
                                <Table.Summary.Row className={TABLE_TOTALS_ROW_CLASS_NAME}>
                                    <Table.Summary.Cell index={0}>
                                        <TotalsLabel
                                            coverage={`${totals.domainsShowingPermissionsCount} of ${totals.domainCount} active`}
                                            coverageInFull={`${totals.domainsShowingPermissionsCount} of ${totals.domainCount} domains showed a permission prompt`}
                                        />
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        <TotalsFigure text={formatCountInFull(totals.permissionsShown)} />
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="right">
                                        <TotalsFigure text={formatCountInFull(totals.subscriptions)} />
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">
                                        <TotalsFigure text={formatRate(totals.rate)} />
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}
            </DashboardTableCard>
        </TableSectionWell>
    )
}
